import React from 'react';
import {Box, Button, CircularProgress, Menu, MenuItem, Slider, Stack, Typography} from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import IconButton from '@mui/material/IconButton';

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// Equalizer bar heights (relative, 1–10) and animation durations for each of the 12 bars.
// Fixed values so bars don't reorder each render, but vary enough to look organic.
const EQ_BARS = [
    {maxH: 7, dur: 0.55},
    {maxH: 10, dur: 0.40},
    {maxH: 5,  dur: 0.70},
    {maxH: 9,  dur: 0.45},
    {maxH: 6,  dur: 0.60},
    {maxH: 10, dur: 0.38},
    {maxH: 8,  dur: 0.52},
    {maxH: 4,  dur: 0.68},
    {maxH: 9,  dur: 0.43},
    {maxH: 6,  dur: 0.58},
    {maxH: 10, dur: 0.42},
    {maxH: 7,  dur: 0.62},
];

const EQ_KEYFRAMES = `
@keyframes eq-bounce {
    0%, 100% { transform: scaleY(0.15); }
    50%       { transform: scaleY(1); }
}`;

function AudioEqualizer({playing}) {
    return (
        <>
            <style>{EQ_KEYFRAMES}</style>
            <Box
                aria-hidden="true"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '1.5px',
                    height: 18,
                    px: 0.25,
                    flexShrink: 0,
                }}
            >
                {EQ_BARS.map((bar, i) => (
                    <Box
                        key={i}
                        sx={{
                            width: 2.5,
                            height: `${bar.maxH * 10}%`,
                            bgcolor: 'primary.main',
                            borderRadius: '1.5px 1.5px 0 0',
                            transformOrigin: 'bottom',
                            transform: playing ? undefined : 'scaleY(0.15)',
                            animation: playing
                                ? `eq-bounce ${bar.dur}s ease-in-out ${(i * 0.04).toFixed(2)}s infinite alternate`
                                : 'none',
                            opacity: playing ? 0.85 : 0.3,
                            transition: 'opacity 0.3s',
                        }}
                    />
                ))}
            </Box>
        </>
    );
}

function formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const SourcesMediaPlayer = React.forwardRef(function SourcesMediaPlayer(
    {src, mimeType, fileType, isLoading, error, playerHeight, initialDurationMs = null, posterUrl = null},
    ref,
) {
    const [playing, setPlaying] = React.useState(false);
    // True while the browser is fetching/decoding bytes and has nothing to render yet — driven
    // by the media element's own waiting/canplay/playing events rather than our `play` click
    // handler, so it reflects actual buffering state instead of "play() was called". Without
    // this, clicking Play flips the icon to Pause immediately while the video stays frozen for
    // however long the initial buffer takes, with zero feedback that it's still loading.
    const [buffering, setBuffering] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(() => (initialDurationMs ? initialDurationMs / 1000 : 0));
    const [playbackRate, setPlaybackRate] = React.useState(1);
    const [speedAnchor, setSpeedAnchor] = React.useState(null);
    // Accept IANA prefixes (video/*), bare kind labels ('video'), or resolved fileType
    const isVideo = mimeType?.startsWith('video/') || mimeType === 'video' || fileType === 'video';

    // Attach event listeners once on mount (ref is stable after forwardRef resolves)
    React.useEffect(() => {
        const media = ref?.current;
        if (!media) return;
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        const onEnded = () => setPlaying(false);
        const onTimeUpdate = () => setCurrentTime(media.currentTime);
        const onDurationChange = () => setDuration(media.duration || 0);
        const onWaiting = () => setBuffering(true);
        const onCanPlay = () => setBuffering(false);
        const onPlaying = () => setBuffering(false);
        const onError = () => {
            setBuffering(false);
            console.error('[MediaPlayer] media error:', media.error?.code, media.error?.message, '| src=', media.src);
        };
        media.addEventListener('play', onPlay);
        media.addEventListener('pause', onPause);
        media.addEventListener('ended', onEnded);
        media.addEventListener('timeupdate', onTimeUpdate);
        media.addEventListener('durationchange', onDurationChange);
        media.addEventListener('waiting', onWaiting);
        media.addEventListener('canplay', onCanPlay);
        media.addEventListener('playing', onPlaying);
        media.addEventListener('error', onError);
        return () => {
            media.removeEventListener('play', onPlay);
            media.removeEventListener('pause', onPause);
            media.removeEventListener('ended', onEnded);
            media.removeEventListener('timeupdate', onTimeUpdate);
            media.removeEventListener('durationchange', onDurationChange);
            media.removeEventListener('waiting', onWaiting);
            media.removeEventListener('canplay', onCanPlay);
            media.removeEventListener('playing', onPlaying);
            media.removeEventListener('error', onError);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    // Reset player state whenever src changes. We do NOT assign media.src here —
    // that is deferred to the first togglePlay call so no network requests are made
    // until the user explicitly presses Play. Duration is seeded from the server-provided
    // initialDurationMs (already in the DB from the transcription pipeline) so the
    // scrubber shows the correct range immediately with zero video byte requests.
    React.useEffect(() => {
        const media = ref?.current;
        setPlaying(false);
        setBuffering(false);
        setCurrentTime(0);
        setDuration(initialDurationMs ? initialDurationMs / 1000 : 0);
        setPlaybackRate(1);
        if (!media) return;
        // Hard-stop whatever the previously selected item was playing before we
        // swap in the new source. Pausing first cancels any in-flight play()
        // (including a pending canplay-driven play from the transcript-sync hook),
        // so switching audio→audio / video→audio never leaves the old media audible.
        try {
            media.pause();
        } catch {}
        media.removeAttribute('src');
        media.load();
    }, [src, initialDurationMs, ref]);

    const handleSpeedChange = (rate) => {
        setPlaybackRate(rate);
        setSpeedAnchor(null);
        const media = ref?.current;
        if (media) media.playbackRate = rate;
    };

    const togglePlay = () => {
        const media = ref?.current;
        if (!media) {
            console.warn('[MediaPlayer] togglePlay: ref.current is null — audio element not mounted');
            return;
        }

        if (media.paused) {
            if (media.error) {
                // Recover from error state by reloading before playing
                if (src && !media.src) {
                    media.src = src;
                }
                media.load();
                media.addEventListener('canplay', () => {
                    media.playbackRate = playbackRate;
                    media.play().catch((err) => console.error('[MediaPlayer] play() rejected:', err));
                }, {once: true});
            } else {
                // Assign src lazily on first play — deferred from file-open to avoid the
                // browser's moov-atom binary search until the user actually wants to play.
                // Do NOT call media.load() here: the browser streams on demand when play()
                // is called. Calling load() forces a full restart of the network request and
                // causes a "waiting for canplay" delay that looks like a full-file download.
                // Check media.src (not a private ref) — the transcript-sync hook's seekTo()
                // may have already assigned it directly (e.g. deep-linking from a search hit),
                // and re-assigning here would restart the load algorithm from scratch, freezing
                // playback on frame 0 while a brand-new set of range requests streams in.
                if (src && !media.src) {
                    media.src = src;
                    media.playbackRate = playbackRate;
                }
                if (src) {
                    media.play().catch((err) => console.error('[MediaPlayer] play() rejected:', err));
                }
            }
        } else {
            media.pause();
        }
    };

    const handleSliderChange = (_, value) => {
        const media = ref?.current;
        if (media) {
            // Assign src on first seek too, so scrubbing before Play works correctly
            if (src && !media.src) {
                media.src = src;
                media.playbackRate = playbackRate;
            }
            media.currentTime = value;
        }
        setCurrentTime(value);
    };

    if (error) {
        return (
            <Box sx={{p: 2}}>
                <Typography variant="caption" color="error">{error}</Typography>
            </Box>
        );
    }

    const speedMenu = (
        <Menu
            anchorEl={speedAnchor}
            open={Boolean(speedAnchor)}
            onClose={() => setSpeedAnchor(null)}
            anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
            slotProps={{paper: {sx: {minWidth: 80}}}}
        >
            {SPEED_OPTIONS.map((rate) => (
                <MenuItem
                    key={rate}
                    selected={rate === playbackRate}
                    dense
                    onClick={() => handleSpeedChange(rate)}
                    sx={{justifyContent: 'center', fontSize: '0.8rem'}}
                >
                    {rate}×
                </MenuItem>
            ))}
        </Menu>
    );

    // Audio-only: compact single-row strip with equalizer
    if (!isVideo) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={(theme) => ({
                    bgcolor: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    px: 1,
                    py: 0.5,
                    flexShrink: 0,
                    height: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                })}
            >
                {src && <audio ref={ref} preload="none" style={{display: 'none'}}/>}

                <AudioEqualizer playing={playing}/>

                {isLoading || buffering ? (
                    <CircularProgress size={20} sx={{flexShrink: 0}}/>
                ) : (
                    <IconButton size="small" onClick={togglePlay} disabled={!src} aria-label={playing ? 'Pause' : 'Play'} sx={{flexShrink: 0}}>
                        {playing ? <PauseRoundedIcon fontSize="small"/> : <PlayArrowRoundedIcon fontSize="small"/>}
                    </IconButton>
                )}

                <Typography variant="caption" sx={{minWidth: 28, textAlign: 'right', flexShrink: 0, fontSize: '0.65rem'}} color="text.secondary">
                    {formatTime(currentTime)}
                </Typography>

                <Slider
                    size="small"
                    min={0}
                    max={duration || 1}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSliderChange}
                    disabled={!src || duration === 0}
                    sx={{flex: 1, minWidth: 40}}
                    aria-label="Playback position"
                />

                <Typography variant="caption" sx={{minWidth: 28, flexShrink: 0, fontSize: '0.65rem'}} color="text.secondary">
                    {formatTime(duration)}
                </Typography>

                <Button
                    size="small"
                    disabled={!src}
                    onClick={(e) => setSpeedAnchor(e.currentTarget)}
                    sx={{
                        minWidth: 0,
                        px: 0.5,
                        py: 0,
                        fontSize: '0.62rem',
                        fontWeight: playbackRate !== 1 ? 700 : 400,
                        color: playbackRate !== 1 ? 'primary.main' : 'text.secondary',
                        textTransform: 'none',
                        lineHeight: 1.5,
                        flexShrink: 0,
                    }}
                    aria-label="Playback speed"
                >
                    {playbackRate === 1 ? '1×' : `${playbackRate}×`}
                </Button>
                {speedMenu}
            </Stack>
        );
    }

    // Video: full layout with video element + controls below
    return (
        <Stack
            spacing={0}
            sx={(theme) => ({
                bgcolor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                px: 2,
                py: 1.5,
                flexShrink: 0,
                height: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
            })}
        >
            {/* Render media elements WITHOUT src attribute in JSX — src is assigned imperatively
                in the useEffect above after mount, which avoids MediaError{code:4} during render. */}
            {src && (
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    mb: 1,
                    bgcolor: '#000',
                    borderRadius: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}>
                    <video
                        ref={ref}
                        style={{width: '100%', height: '100%', display: 'block', objectFit: 'contain'}}
                        preload="metadata"
                        poster={posterUrl || undefined}
                    />
                    {buffering && (
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0, 0, 0, 0.35)',
                            pointerEvents: 'none',
                        }}>
                            <CircularProgress size={36} sx={{color: 'common.white'}}/>
                        </Box>
                    )}
                </Box>
            )}

            <Stack direction="row" alignItems="center" spacing={1}>
                {isLoading ? (
                    <CircularProgress size={24}/>
                ) : (
                    <IconButton size="small" onClick={togglePlay} disabled={!src} aria-label={playing ? 'Pause' : 'Play'}>
                        {playing ? <PauseRoundedIcon/> : <PlayArrowRoundedIcon/>}
                    </IconButton>
                )}
                <Typography variant="caption" sx={{minWidth: 36, textAlign: 'right'}} color="text.secondary">
                    {formatTime(currentTime)}
                </Typography>
                <Slider
                    size="small"
                    min={0}
                    max={duration || 1}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSliderChange}
                    disabled={!src || duration === 0}
                    sx={{flex: 1}}
                    aria-label="Playback position"
                />
                <Typography variant="caption" sx={{minWidth: 36}} color="text.secondary">
                    {formatTime(duration)}
                </Typography>
                <Button
                    size="small"
                    disabled={!src}
                    onClick={(e) => setSpeedAnchor(e.currentTarget)}
                    sx={{
                        minWidth: 0,
                        px: 0.75,
                        py: 0,
                        fontSize: '0.7rem',
                        fontWeight: playbackRate !== 1 ? 700 : 400,
                        color: playbackRate !== 1 ? 'primary.main' : 'text.secondary',
                        textTransform: 'none',
                        lineHeight: 1.5,
                    }}
                    aria-label="Playback speed"
                >
                    {playbackRate === 1 ? '1×' : `${playbackRate}×`}
                </Button>
                {speedMenu}
            </Stack>
        </Stack>
    );
});

export default SourcesMediaPlayer;
